import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { initialResponseState, ResponseState, LoadingState } from 'store/fetchingLogic'
import { VERIFY_INTERVAL } from 'gatsby-env-variables'
import { devicesApi } from 'store/api/devices/devices'
import { DeviceInfo } from 'store/api/devices/devices.interface'
import { sessionLogout } from '../session'
import { RootState } from 'store/rootReducer'
import { GroupingType } from 'components/Dashboard/Clients/GroupingDropdown'
import xor from 'lodash/xor'

const devicesPollingIntervalMs = 2_000

export enum SortDevicesType {
  ALPHABETICAL = 'Alphabetical',
  LAST_ACTIVE = 'Last Active',
  ENFORCED_PROFILE = 'Enforced Profile',
  CTRLD_VERSION = 'ctrld Version',
}

export enum SelectMultipleDevicesDialogType {
  DELETE = 'delete',
  EDIT = 'edit',
}

export interface DeviceState extends ResponseState<{ devices: DeviceInfo[] }> {
  showScheduleDeviceTray: boolean
  editDeviceId?: string
  configuredDevice?: DeviceInfo | undefined | null
  checkConfiguredDeviceLoadingState?: LoadingState
  sortDevicesBy?: SortDevicesType
  shouldSortClientsAlphabetically?: boolean
  isDeviceStatusVerified: boolean
  groupClientsBy: string
  latestCtrldVersion?: string
  shouldOpenAdvancedSettings: boolean
  isSelecting: boolean
  selectedDeviceIds: string[]
  selectMultipleDevicesActiveDialog?: SelectMultipleDevicesDialogType
}

const initialState: DeviceState = {
  ...initialResponseState,
  configuredDevice: null, // uninitialized value
  showScheduleDeviceTray: false,
  sortDevicesBy: SortDevicesType.ALPHABETICAL,
  shouldSortClientsAlphabetically: true,
  isDeviceStatusVerified: false,
  groupClientsBy: GroupingType.NO,
  shouldOpenAdvancedSettings: false,
  isSelecting: false,
  selectedDeviceIds: [],
}

let devicePollingResponse
export const startPollingDevices = createAsyncThunk(
  'devices/startPolling',
  async (
    { deviceId, shouldSubscribe = true }: { deviceId?: string; shouldSubscribe?: boolean },
    { dispatch },
  ): Promise<void> => {
    if (!shouldSubscribe) {
      devicePollingResponse.unsubscribe()
      dispatch(detectConfiguredDevice(false))
    } else {
      devicePollingResponse = dispatch(
        devicesApi.endpoints.getDevice.initiate(deviceId, {
          subscribe: true,
          subscriptionOptions: {
            pollingInterval: devicesPollingIntervalMs,
          },
        }),
      )
      dispatch(detectConfiguredDevice(true))
    }
  },
)

export const stopPollingDevices = createAsyncThunk(
  'devices/stopPolling',
  async (_, { dispatch }) => {
    dispatch(startPollingDevices({ shouldSubscribe: false }))
  },
)

let configuredDeviceResponse
export const detectConfiguredDevice = createAsyncThunk(
  'devices/detectConfiguredDevice',
  async (shouldSubscribe: boolean | undefined = true, { dispatch, getState }) => {
    const configuredDevice = (getState() as RootState).devices.configuredDevice

    if (!shouldSubscribe) {
      configuredDeviceResponse?.unsubscribe()
    } else {
      if (configuredDeviceResponse) {
        configuredDeviceResponse?.unsubscribe()
      }
      configuredDeviceResponse = dispatch(
        devicesApi.endpoints.detectDevice.initiate('', {
          subscribe: true,
          subscriptionOptions: {
            pollingInterval: configuredDevice
              ? undefined
              : VERIFY_INTERVAL || devicesPollingIntervalMs,
          },
        }),
      )
    }

    return
  },
)

export const scrubProfilesInDevice = (device: DeviceInfo): DeviceInfo => {
  const scrubbedDevice = {
    ...device,
    profile_id: device.profile_id?.toString(),
    profile_id2: device.profile_id2?.toString(),
  }
  if (scrubbedDevice.profile?.PK) {
    scrubbedDevice.profile = { ...scrubbedDevice.profile, PK: scrubbedDevice.profile.PK.toString() }
  }
  if (scrubbedDevice.profile2?.PK) {
    scrubbedDevice.profile2 = {
      ...scrubbedDevice.profile2,
      PK: scrubbedDevice.profile2.PK.toString(),
    }
  }
  return scrubbedDevice
}

export const deviceSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    clearError(state): void {
      state.error = undefined
    },
    toggleScheduleDeviceTray(state, action: PayloadAction<boolean | undefined>): void {
      state.showScheduleDeviceTray = action.payload ?? !state.showScheduleDeviceTray
    },
    setDeviceForEdit(state, action: PayloadAction<string | undefined>): void {
      state.editDeviceId = action.payload
    },
    setSortDevicesBy(state, action: PayloadAction<SortDevicesType>): void {
      state.sortDevicesBy = action.payload
    },
    setShouldSortClientsAlphabetically(state, action: PayloadAction<boolean>): void {
      state.shouldSortClientsAlphabetically = action.payload
    },
    setGroupClientsBy(state, action: PayloadAction<GroupingType>): void {
      state.groupClientsBy = action.payload
    },
    setIsDeviceStatusVerified(state, action: PayloadAction<boolean>): void {
      state.isDeviceStatusVerified = action.payload
    },
    setConfigureDevice(state, action: PayloadAction<DeviceInfo | undefined>): void {
      state.configuredDevice = action.payload
    },
    setLatestCtrldVersion(state, action: PayloadAction<string>): void {
      state.latestCtrldVersion = action.payload
    },
    setShouldOpenAdvancedSettings(state, action: PayloadAction<boolean>): void {
      state.shouldOpenAdvancedSettings = action.payload
    },
    clearDevices(state): void {
      state.data && (state.data.devices = [])
    },
    selectOrDeselectDevice(state, action: PayloadAction<string>): void {
      state.selectedDeviceIds = xor(state.selectedDeviceIds, [action.payload?.toString()])
      state.isSelecting = !!state.selectedDeviceIds.length
    },
    setSelectMultipleDevicesActiveDialog(
      state,
      action: PayloadAction<SelectMultipleDevicesDialogType | undefined>,
    ): void {
      state.selectMultipleDevicesActiveDialog = action?.payload
    },
    deselectAllDevices(state): void {
      state.isSelecting = false
      state.selectedDeviceIds = []
    },
    selectAllDevices(state, action: PayloadAction<DeviceInfo[] | []>): void {
      state.isSelecting = true
      state.selectedDeviceIds = action.payload.map(x => x.PK?.toString()) || []
    },
    resetDeviceSelectionMode(state): void {
      state.selectedDeviceIds = []
      state.isSelecting = false
    },
  },
  extraReducers: builder => {
    builder
      .addCase(sessionLogout.fulfilled, () => initialState)
      .addMatcher(devicesApi.endpoints.deleteDevice.matchFulfilled, (state, action) => {
        if (action.meta.arg.originalArgs.pk == state.configuredDevice?.PK) {
          state.configuredDevice = undefined
        }
      })
  },
})

export const {
  toggleScheduleDeviceTray,
  setDeviceForEdit,
  clearDevices,
  setSortDevicesBy,
  setShouldSortClientsAlphabetically,
  setIsDeviceStatusVerified,
  setConfigureDevice,
  setGroupClientsBy,
  setLatestCtrldVersion,
  setShouldOpenAdvancedSettings,
  selectOrDeselectDevice,
  setSelectMultipleDevicesActiveDialog,
  resetDeviceSelectionMode,
  deselectAllDevices,
  selectAllDevices,
} = deviceSlice.actions
